// Due dates (OEM-267): an item may have a day it's due (a calendar day, no time: Item.dueOn). "Today"
// and "this week" are the board's (REMINDER_TZ, America/New_York by default), so a date reads the same
// for everyone wherever they are. A week runs Monday to Sunday. Pure, Intl only: the server, the web
// app and Claude's tools all use it.

import { DAY, localDate, MONTHS, WEEKDAYS } from "./time.ts";

/** The board's time zone when nothing says otherwise (REMINDER_TZ's default). */
export const DEFAULT_TIME_ZONE = "America/New_York";

/** A due date: "2026-10-21". */
export type IsoDay = string;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "2026-10-21" is a real calendar day (not "2026-02-30", not "21/10/2026"). */
export function isIsoDay(s: unknown): s is IsoDay {
  if (typeof s !== "string") return false;
  const m = s.match(ISO_DAY);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

/** Days since 1970-01-01 of a calendar day (no time zone: it's a day, not an instant). */
const dayIndex = (iso: IsoDay) => Math.round(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) / DAY);
const fromIndex = (n: number): IsoDay => new Date(n * DAY).toISOString().slice(0, 10);

/** `iso` moved by n days. */
export const addDays = (iso: IsoDay, n: number): IsoDay => fromIndex(dayIndex(iso) + n);

/** Days from `a` to `b` (b - a). */
export const daysBetween = (a: IsoDay, b: IsoDay) => dayIndex(b) - dayIndex(a);

/** 0 = Sunday ... 6 = Saturday. */
export const weekdayOf = (iso: IsoDay) => new Date(dayIndex(iso) * DAY).getUTCDay();

/** Today on the board: the calendar day it is now in its time zone. */
export const todayIn = (timeZone: string = DEFAULT_TIME_ZONE, now: Date = new Date()): IsoDay => localDate(now, timeZone);

/** The last day of the week `today` is in: its Sunday (a week runs Monday to Sunday). */
export const weekEnd = (today: IsoDay): IsoDay => addDays(today, (7 - weekdayOf(today)) % 7);

/** A Prisma DATE column's value as a day: it comes back as midnight UTC. */
export const isoOfDate = (d: Date | null | undefined): IsoDay | null => (d ? d.toISOString().slice(0, 10) : null);

/** A day as the DATE column takes it. */
export const dateOfIso = (iso: IsoDay): Date => new Date(`${iso}T00:00:00.000Z`);

/** "Oct 21" (with ", 2027" when it isn't `thisYear`). */
export function shortDay(iso: IsoDay, thisYear?: number): string {
  const y = Number(iso.slice(0, 4));
  return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}${thisYear != null && y !== thisYear ? `, ${y}` : ""}`;
}

/** "Wed Oct 21": how the history and Claude say a due date. */
export const longDay = (iso: IsoDay, thisYear?: number) => `${WEEKDAYS[weekdayOf(iso)]} ${shortDay(iso, thisYear)}`;

/** Where a due date falls from today: past it, today, later this week, after this week, or no date. */
export type DueBucket = "overdue" | "today" | "week" | "later" | "none";

export function dueBucket(due: IsoDay | null | undefined, today: IsoDay): DueBucket {
  if (!due) return "none";
  if (due < today) return "overdue";
  if (due === today) return "today";
  return due <= weekEnd(today) ? "week" : "later";
}

/** Past its due date and still open: closed items are never overdue. */
export const isOverdue = (it: { due?: IsoDay | null; status: string }, today: IsoDay) => it.status === "open" && !!it.due && it.due < today;

/** Due today or later this week (to Sunday), open or not. */
export const isDueThisWeek = (it: { due?: IsoDay | null }, today: IsoDay) => !!it.due && it.due >= today && it.due <= weekEnd(today);

/** The board's "Due" filter: Overdue / Due this week / Due later / No date. */
export type DueFilter = "overdue" | "week" | "later" | "none";
export const DUE_FILTERS: { id: DueFilter; label: string; hint: string }[] = [
  { id: "overdue", label: "Overdue", hint: "Open, and its due date has passed" },
  { id: "week", label: "Due this week", hint: "Due today or later this week (to Sunday)" },
  { id: "later", label: "Due later", hint: "Due after this week" },
  { id: "none", label: "No date", hint: "No due date set" },
];
export const isDueFilter = (v: unknown): v is DueFilter => v === "overdue" || v === "week" || v === "later" || v === "none";

export function matchesDue(it: { due?: IsoDay | null; status: string }, f: DueFilter, today: IsoDay): boolean {
  switch (f) {
    case "overdue":
      return isOverdue(it, today);
    case "week":
      return isDueThisWeek(it, today);
    case "later":
      return !!it.due && it.due > weekEnd(today);
    case "none":
      return !it.due;
  }
}

/** Soonest first, no date last. */
export function compareDue(a: IsoDay | null | undefined, b: IsoDay | null | undefined): number {
  if (a === b || (!a && !b)) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a < b ? -1 : 1;
}

/** The card's due badge: "overdue 2d" (red), "due today", "due Wed" (this coming week), "due Oct 21". */
export interface DueBadge {
  text: string;
  tone: "overdue" | "today" | "soon" | "later" | "closed";
  /** the full date, for a tooltip: "Due Wed Oct 21" */
  title: string;
}

/** What the badge says, or null without a date. A closed item shows its date quietly, never overdue. */
export function dueBadge(it: { due?: IsoDay | null; status: string }, today: IsoDay): DueBadge | null {
  const due = it.due;
  if (!due) return null;
  const year = Number(today.slice(0, 4));
  const title = `Due ${longDay(due, year)}`;
  if (it.status !== "open") return { text: `due ${shortDay(due, year)}`, tone: "closed", title };
  const n = daysBetween(today, due);
  if (n < 0) return { text: `overdue ${-n}d`, tone: "overdue", title: `${title}: ${-n} day${n === -1 ? "" : "s"} ago` };
  if (n === 0) return { text: "due today", tone: "today", title };
  if (n <= 6) return { text: `due ${WEEKDAYS[weekdayOf(due)]}`, tone: "soon", title };
  return { text: `due ${shortDay(due, year)}`, tone: "later", title };
}

/** What setting a due date says in the history: "set the due date to Wed Oct 21", "cleared the due date". */
export function dueEventText(from: IsoDay | null | undefined, to: IsoDay | null | undefined): string {
  if (!to) return from ? `cleared the due date (was ${longDay(from)})` : "cleared the due date";
  return from ? `moved the due date from ${longDay(from)} to ${longDay(to)}` : `set the due date to ${longDay(to)}`;
}
